import React, { TextareaHTMLAttributes, forwardRef, useId } from 'react';

import { TextArea as RadixTextArea } from '@radix-ui/themes';
import clsx from 'clsx';

import { HelpText } from './HelpText';
import { Label } from './Label';

const sizeMapping = {
  small: '2',
  medium: '2',
  large: '3',
  default: '3',
} as const;

const variantMapping = {
  filled: 'soft',
  outlined: 'surface',
  ghost: 'surface',
  classic: 'classic',
} as const;

const colorMapping = {
  default: 'gray',
  error: 'ruby',
} as const;

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size' | 'color'> {
  label?: string;
  placeholder?: string;
  required?: boolean;
  size?: keyof typeof sizeMapping;
  color?: keyof typeof colorMapping;
  variant?: keyof typeof variantMapping;
  error?: boolean;
  helpText?: string;
  errorMessage?: string;
  className?: string;
  value?: string;
  defaultValue?: string;
}

/**
 * TextArea component is a design system adapter for Radix TextArea.
 * It supports design system props mapping and is ready for extension.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      placeholder,
      required,
      size = 'large',
      color = 'default',
      variant = 'outlined',
      error = false,
      helpText,
      errorMessage,
      className,
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const inputId = useId();
    const mappedSize = sizeMapping[size] || sizeMapping.default;
    const mappedVariant = variantMapping[variant] || variantMapping.outlined;
    const mappedColor = error
      ? colorMapping.error
      : colorMapping[color] || colorMapping.default;

    return (
      <div className={clsx('textarea-wrapper', className)}>
        {label && (
          <Label htmlFor={inputId} className={clsx({ required })}>
            {label} {required && <span className="textarea-required">*</span>}
          </Label>
        )}
        <RadixTextArea
          id={inputId}
          placeholder={placeholder}
          color={mappedColor as typeof mappedColor}
          size={mappedSize as typeof mappedSize}
          variant={mappedVariant as typeof mappedVariant}
          radius="large"
          ref={ref}
          className={clsx(
            `color-${color}`,
            `size-${size}`,
            `variant-${variant}`,
            { error },
            'textarea-root'
          )}
          data-error={error}
          value={value}
          defaultValue={defaultValue}
          {...props}
        />
        {(errorMessage || helpText) && (
          <HelpText error={error}>{error ? errorMessage : helpText}</HelpText>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
